import {
  BitkitIconComponent,
  IconAndroid,
  IconApple,
  IconDoc,
  IconFilePlist,
  IconFileZip,
  IconImage,
} from '@bitrise/bitkit-v2';
import { Text } from '@chakra-ui/react/text';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import {
  AppInfo,
  Artifact,
  ArtifactDevice,
  DecoratedArtifact,
  DecoratedArtifactDevice,
  DecoratedArtifactDevices,
  IMAGE_EXTENSIONS,
  TEXT_EXTENSIONS,
} from '@/models/Artifact';
import { monolithApi } from '@/utils/api';
import { formatDate } from '@/utils/commonFormats';
import { formatBytes } from '@/utils/formatBytes';
import { getArtifactPathForBuild, getArtifactPathForPipeline } from '@/utils/monolithApiRouteService';

type UseArtifactsProps = {
  enabled: boolean;
  appSlug?: string;
  buildSlug?: string;
  pipelineSlug?: string;
  attemptId?: string;
};

type HookResult = {
  artifacts: DecoratedArtifact[];
  isError: boolean;
  isFetched: boolean;
  isLoading: boolean;
  otherArtifacts: DecoratedArtifact[];
  installableArtifacts: DecoratedArtifact[];
  intermediateFiles: DecoratedArtifact[];
};

type User = {
  email: string;
  username: string;
};

const distributionTypes: Record<string, { title: string; description?: string }> = {
  'app-store': {
    title: 'App Store',
    description:
      "You can't install it on any device, but you can download the App's .ipa and upload it into the App Store.",
  },
  development: {
    title: 'Development',
    description:
      'It can only be installed on devices which are included in the Provisioning Profile - listed in the “Who can install this app?” section.',
  },
  enterprise: {
    title: 'Enterprise',
    description: 'It can be installed by anyone.',
  },
  'ad-hoc': {
    title: 'Ad Hoc',
    description:
      'It can only be installed on devices which are included in the Provisioning Profile - listed in the “Who can install this app?” section.',
  },
  unknown: {
    title: 'Unknown',
  },
};

const getMetaByExtension = (
  artifactMeta: any,
  appleDeviceFamiliesText: string[],
  extension: string,
  distributionType: string,
): AppInfo[] => {
  if (!artifactMeta) {
    return [];
  }

  if (extension === 'apk') {
    return [
      {
        key: 'App title',
        value: artifactMeta.appInfo.appName,
      },
      {
        key: 'Application ID',
        value: artifactMeta.appInfo.packageName,
      },
      {
        key: 'Version code',
        value: artifactMeta.appInfo.versionCode,
      },
      {
        key: 'Version name',
        value: artifactMeta.appInfo.versionName,
      },
      {
        key: 'Min SDK version',
        value: artifactMeta.appInfo.minSdkVersion,
      },
      {
        key: 'Size',
        value: formatBytes(Number(artifactMeta.fileSizeBytes)),
      },
      {
        key: 'Signed by',
        value: artifactMeta.signedBy,
      },
    ];
  }

  if (extension === 'ipa') {
    const distributionTypeData = distributionTypes[distributionType] ?? distributionTypes.unknown;

    return [
      {
        key: 'App Title',
        value: artifactMeta.appInfo.appTitle,
      },
      {
        key: 'Distribution type',
        value: distributionTypeData.title,
        description: distributionTypeData.description,
      },
      {
        key: 'Bundle ID',
        value: artifactMeta.appInfo.bundleId,
      },
      {
        key: 'Version',
        value: <Text wordBreak="break-all">v{artifactMeta.appInfo.version}</Text>,
      },
      {
        key: 'Build number',
        value: `#${artifactMeta.appInfo.buildNumber}`,
      },
      {
        key: 'Size',
        value: formatBytes(artifactMeta.fileSizeBytes),
      },
      {
        key: 'Minimum OS',
        value: artifactMeta.appInfo.minOsVersion,
      },
      {
        key: 'Supported device types',
        value: appleDeviceFamiliesText.join(', '),
      },
      {
        key: 'Certificate expires',
        value: formatDate(artifactMeta.provisioningInfo?.expireDate),
      },
    ];
  }

  if (extension === 'aab') {
    return [
      {
        key: 'App Title',
        value: artifactMeta.appInfo.appName,
      },
      {
        key: 'Application ID',
        value: artifactMeta.appInfo.packageName,
      },
      {
        key: 'Version code',
        value: artifactMeta.appInfo.versionCode,
      },
      {
        key: 'Version name',
        value: artifactMeta.appInfo.versionName,
      },
      {
        key: 'Size',
        value: formatBytes(artifactMeta.fileSizeBytes),
      },
      {
        key: 'Min SDK version',
        value: artifactMeta.appInfo.minSdkVersion,
      },
      {
        key: 'Module',
        value: artifactMeta.module,
      },
      {
        key: 'Build type',
        value: artifactMeta.buildType,
      },
      {
        key: 'Signed by',
        value: artifactMeta.signedBy,
      },
    ];
  }

  return [];
};

const getDevices = (
  associated: ArtifactDevice[] = [],
  nonAssociated: ArtifactDevice[] = [],
): DecoratedArtifactDevices => {
  const users: { [key: string]: DecoratedArtifactDevice[] } = {};
  const deviceCount = associated.length + nonAssociated.length;

  associated.forEach(({ deviceIdentifier, owner, title }) => {
    if (!owner?.username) {
      return;
    }

    if (!users[owner.username]) {
      users[owner.username] = [];
    }
    users[owner.username].push({
      name: title || 'Untitled device',
      udid: deviceIdentifier,
    });
  });

  const userCount = Object.keys(users).length;

  if (nonAssociated.length > 0) {
    const nonAssociatedName = 'No user is associated with this device.';
    users[nonAssociatedName] = nonAssociated.map(({ deviceIdentifier, title }) => ({
      name: title || 'Untitled device',
      udid: deviceIdentifier,
    }));
  }

  return {
    deviceCount,
    userCount,
    users,
  };
};

export const getNotifiedEmails = (emails: string[], users: User[]): string[] => {
  if (!Array.isArray(emails) || !Array.isArray(users)) {
    return [];
  }
  return [...emails, ...users.map(({ email }: User) => email)];
};

const getContentType = (artifact: Artifact, extension: string): 'installable' | 'text' | 'image' | undefined => {
  switch (true) {
    case artifact.isInstallable:
      return 'installable';
    case TEXT_EXTENSIONS.includes(extension):
      return 'text';
    case IMAGE_EXTENSIONS.includes(extension):
      return 'image';
    default:
      return undefined;
  }
};

const getIconType = (extension: string): BitkitIconComponent => {
  if (extension === 'apk' || extension === 'aab') {
    return IconAndroid;
  }
  if (extension === 'ipa') {
    return IconApple;
  }
  if (extension === 'plist') {
    return IconFilePlist;
  }
  if (extension === 'zip') {
    return IconFileZip;
  }
  if (IMAGE_EXTENSIONS.includes(extension)) {
    return IconImage;
  }
  return IconDoc;
};

export const decorateArtifactData = (artifact: Artifact): DecoratedArtifact => {
  const { artifactMeta: meta, publicInstallPageUrl = '', isPublicPageEnabled = false } = artifact;
  const extension = artifact.title.split('.').pop() || '';
  const appInfo = getMetaByExtension(
    meta,
    artifact.appleDeviceFamiliesText || [],
    extension,
    artifact.distributionType,
  );
  let version = meta?.appInfo?.version ? `v${meta?.appInfo?.version}` : 'N/A';
  if (['apk', 'aab'].includes(extension) && meta?.appInfo?.versionName) {
    version = meta.appInfo.versionName;
  }
  const size = formatBytes(
    meta?.fileSizeBytes !== undefined ? parseFloat(meta?.fileSizeBytes) : artifact.fileSizeBytes,
  );

  return {
    ...artifact,
    allowedTestDevices: getDevices(artifact.allowedTestDevices?.associated, artifact.allowedTestDevices?.nonAssociated),
    appInfo,
    extension,
    metadata: {
      installType: meta?.installType,
    },
    notifiedEmails: getNotifiedEmails(artifact.notifiedEmails, artifact.notifiedUsers),
    isPublicPageEnabled,
    publicInstallPageUrl,
    contentType: getContentType(artifact, extension),
    extensionIconType: getIconType(extension),
    version,
    size,
    type: artifact.artifactType.split('-').pop() || '',
  };
};

type ArtifactsResponse = { data: Artifact[] };

const artifactSelector = (response: ArtifactsResponse): DecoratedArtifact[] => {
  return response.data.map((artifact) => decorateArtifactData(artifact));
};

const useArtifacts = ({ appSlug, buildSlug, pipelineSlug, attemptId, enabled }: UseArtifactsProps): HookResult => {
  const url = getArtifactPathForBuild(buildSlug) || getArtifactPathForPipeline(appSlug, pipelineSlug, attemptId);
  const queryKey = `artifacts-${buildSlug || pipelineSlug}`;

  const {
    isError,
    isFetched,
    isLoading,
    data: artifacts,
  } = useQuery({
    queryKey: [queryKey, url],
    queryFn: () => monolithApi.get(url),
    enabled,
    initialData: { data: [] },
    select: artifactSelector,
  });

  const { otherArtifacts, installableArtifacts, intermediateFiles } = useMemo(() => {
    const installable: DecoratedArtifact[] = [];
    const other: DecoratedArtifact[] = [];
    const intermediate: DecoratedArtifact[] = [];

    artifacts?.forEach((artifact) => {
      if (artifact.contentType === 'installable' && !artifact.isIntermediateFile) {
        installable.push(artifact);
      } else if (artifact.isIntermediateFile) {
        intermediate.push(artifact);
      } else {
        other.push(artifact);
      }
    });

    return {
      installableArtifacts: installable,
      otherArtifacts: other,
      intermediateFiles: intermediate,
    };
  }, [artifacts]);

  return {
    artifacts: artifacts || [],
    isError,
    isLoading,
    isFetched,
    installableArtifacts,
    otherArtifacts,
    intermediateFiles,
  };
};

export default useArtifacts;
